export type EnvIssueSeverity = 'warning' | 'error';

export type EnvNormalizationIssueCode =
  | 'trimmed-whitespace'
  | 'control-chars'
  | 'internal-space'
  | 'empty-after-trim';

export interface EnvNormalizationIssue {
  label: string;
  message: string;
  severity: EnvIssueSeverity;
  code: EnvNormalizationIssueCode;
}

export interface NormalizedEnvValue {
  label: string;
  raw?: string;
  sanitized?: string;
  issues: EnvNormalizationIssue[];
}

export interface RuntimeEnvSnapshot {
  supabaseUrl: NormalizedEnvValue;
  supabaseAnonKey: NormalizedEnvValue;
  envLabel: NormalizedEnvValue;
  runtimeEnv: NormalizedEnvValue;
  supabaseProjectId: NormalizedEnvValue;
}

interface NormalizeOptions {
  forbidSpaces?: boolean;
  trimSeverity?: EnvIssueSeverity;
  controlCharSeverity?: EnvIssueSeverity;
}

function normalizeEnvValue(
  raw: string | undefined,
  label: string,
  options: NormalizeOptions = {}
): NormalizedEnvValue {
  const issues: EnvNormalizationIssue[] = [];

  if (typeof raw !== 'string' || raw.length === 0) {
    return { label, raw: raw ?? undefined, sanitized: raw ?? undefined, issues };
  }

  let sanitized = raw;

  const trimmed = sanitized.trim();
  if (trimmed !== sanitized) {
    const severity = options.trimSeverity ?? 'warning';
    issues.push({
      label,
      severity,
      code: 'trimmed-whitespace',
      message: `${label} had leading/trailing whitespace trimmed (before: ${sanitized.length}, after: ${trimmed.length}).`
    });
    sanitized = trimmed;
  }

  const withoutControl = sanitized.replace(/[\r\n\t]+/g, '');
  if (withoutControl !== sanitized) {
    const severity = options.controlCharSeverity ?? 'warning';
    issues.push({
      label,
      severity,
      code: 'control-chars',
      message: `${label} contained control characters (\\n/\\r/\\t) removed during normalization.`
    });
    sanitized = withoutControl;
  }

  if (!sanitized.length) {
    issues.push({
      label,
      severity: 'error',
      code: 'empty-after-trim',
      message: `${label} resolves to an empty string after trimming whitespace.`
    });
    return { label, raw, sanitized: undefined, issues };
  }

  if (options.forbidSpaces && sanitized.includes(' ')) {
    issues.push({
      label,
      severity: 'error',
      code: 'internal-space',
      message: `${label} contains spaces after normalization; update the environment variable to remove them.`
    });
  }

  return { label, raw, sanitized, issues };
}

function deriveProjectIdFromUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const host = new URL(value).hostname;
    const match = host.match(/^([a-z0-9-]+)\.supabase\.(co|in)$/i);
    return match?.[1];
  } catch {
    return undefined;
  }
}

function buildSnapshot(): RuntimeEnvSnapshot {
  const derivedProjectId =
    import.meta.env.VITE_SUPABASE_PROJECT_ID ??
    deriveProjectIdFromUrl(import.meta.env.VITE_SUPABASE_URL);
  return {
    supabaseUrl: normalizeEnvValue(import.meta.env.VITE_SUPABASE_URL, 'VITE_SUPABASE_URL'),
    supabaseAnonKey: normalizeEnvValue(import.meta.env.VITE_SUPABASE_ANON_KEY, 'VITE_SUPABASE_ANON_KEY', {
      forbidSpaces: true,
      trimSeverity: 'warning',
      controlCharSeverity: 'error'
    }),
    envLabel: normalizeEnvValue(import.meta.env.VITE_ENV_LABEL, 'VITE_ENV_LABEL'),
    runtimeEnv: normalizeEnvValue(import.meta.env.VITE_RUNTIME_ENV, 'VITE_RUNTIME_ENV'),
    supabaseProjectId: normalizeEnvValue(derivedProjectId, 'VITE_SUPABASE_PROJECT_ID')
  } satisfies RuntimeEnvSnapshot;
}

const snapshot = buildSnapshot();

export function getRuntimeEnvSnapshot(): RuntimeEnvSnapshot {
  return snapshot;
}

export const SUPABASE_URL = snapshot.supabaseUrl.sanitized;
export const SUPABASE_ANON_KEY = snapshot.supabaseAnonKey.sanitized;
export const ENV_LABEL = snapshot.envLabel.sanitized;
export const RUNTIME_ENV = snapshot.runtimeEnv.sanitized;
export const SUPABASE_PROJECT_ID = snapshot.supabaseProjectId.sanitized;

let warningsLogged = false;

export function logEnvNormalizationWarningsOnce(context = 'ENV-NORMALIZER'): void {
  if (warningsLogged) return;
  warningsLogged = true;

  const warningIssues = [
    ...snapshot.supabaseUrl.issues,
    ...snapshot.supabaseAnonKey.issues,
    ...snapshot.envLabel.issues,
    ...snapshot.runtimeEnv.issues,
    ...snapshot.supabaseProjectId.issues
  ].filter(issue => issue.severity === 'warning');

  if (warningIssues.length === 0) return;

  console.warn(`[${context}] Normalized environment values with warnings:`);
  warningIssues.forEach(issue => {
    console.warn(`  • ${issue.label}: ${issue.message}`);
  });
}

export function getEnvNormalizationIssues(): EnvNormalizationIssue[] {
  return [
    ...snapshot.supabaseUrl.issues,
    ...snapshot.supabaseAnonKey.issues,
    ...snapshot.envLabel.issues,
    ...snapshot.runtimeEnv.issues,
    ...snapshot.supabaseProjectId.issues
  ];
}

export function getSupabaseEnvIssues(): EnvNormalizationIssue[] {
  return [...snapshot.supabaseUrl.issues, ...snapshot.supabaseAnonKey.issues];
}

export function ensureSupabaseClientConfig(): { url: string; anonKey: string; issues: EnvNormalizationIssue[] } {
  const url = snapshot.supabaseUrl.sanitized;
  const anonKey = snapshot.supabaseAnonKey.sanitized;

  if (!url || !anonKey) {
    throw new Error('Missing Supabase environment variables after normalization.');
  }

  return {
    url,
    anonKey,
    issues: getSupabaseEnvIssues()
  };
}
